export const CASE_STUDIES = [
  {
    slug: "saas-co",
    company: "SaaS Co",
    industry: "B2B SaaS",
    industryTag: "SaaS",
    category: "SaaS",
    employees: "50 employees",
    headline: "15 hours/week saved with automated data entry",
    excerpt: "SaaS Co deployed Max OS Cloud to eliminate manual data entry and slow response times — saving $4,200/month in operational costs.",
    tags: ["Automation", "Data Entry", "Customer Support", "Cost Savings"],
    metrics: {
      hoursSaved: "15 hrs/week",
      costReduction: "$4,200/mo",
      responseTimeImprovement: "60%",
      satisfaction: "+30%",
    },
    challenge: `SaaS Co's operations team was drowning in manual processes. Every week, employees spent 15+ hours on repetitive data entry — copying information between systems, updating spreadsheets, and manually processing customer requests.

Response times were suffering too. With a growing customer base, the team couldn't keep up with support inquiries. Average response time had ballooned to 8+ hours, and customer satisfaction was declining.

"We were hiring people to do work that should be automated," says James Mitchell, Operations Manager. "Every new customer added more manual work, and we were hitting a wall."`,
    solution: `SaaS Co deployed Max OS Cloud on the Business plan, implementing three key automated workflows:

**Automated Data Entry:** Max OS was trained on SaaS Co's data formats and business rules. It now handles all routine data entry — processing customer information, updating records, and syncing data between systems without human intervention.

**Customer Support Triage:** Max OS monitors all support channels, instantly responding to common questions and routing complex issues to the right team member with full context.

**Proactive Monitoring:** Instead of waiting for problems, Max OS detects potential issues — like data inconsistencies or unusual patterns — and flags them before they impact customers.

The setup took less than a week, and the team was able to start seeing results from day one.`,
    implementation: {
      timeline: "2 weeks",
      steps: [
        { week: "Week 1", title: "Setup & Training", description: "Max OS Cloud provisioned on Business plan. Team trained on workflow configuration. Data formats and business rules documented." },
        { week: "Week 2", title: "Go-Live & Optimization", description: "Automated data entry and support triage activated. Monitoring dashboards configured. Team redirected to strategic work." },
      ],
      channels: ["Email", "Slack"],
    },
    beforeAfter: {
      before: ["15+ hours/week on manual data entry", "8+ hour average response time", "$4,200/month wasted on repetitive tasks", "2 FTEs stuck on routine work"],
      after: ["0 hours on manual data entry (fully automated)", "3.2 hour average response time (-60%)", "$0 wasted — all costs redirected to growth", "2 FTEs focused on strategic initiatives"],
    },
    results: [
      { value: "15 hrs", label: "Saved per week" },
      { value: "60%", label: "Response time reduction" },
      { value: "$4,200", label: "Monthly cost savings" },
      { value: "2 FTEs", label: "Redirected to strategic work" },
    ],
    testimonial: {
      quote: "Max OS Cloud eliminated our most tedious workflows. We've redirected 2 FTEs to strategic work.",
      name: "James Mitchell",
      role: "Operations Manager",
      photo: "https://images.unsplash.com/photo-1614010966237-74489a16848b?crop=entropy&cs=srgb&fm=jpg&q=85&w=200&h=200&fit=crop",
    },
  },
  {
    slug: "shopdirect",
    company: "ShopDirect",
    industry: "E-commerce",
    industryTag: "E-commerce",
    category: "E-commerce",
    employees: "200 orders/day",
    headline: "40% support ticket reduction with 24/7 AI coverage",
    excerpt: "ShopDirect deployed Max OS Cloud on WhatsApp and Email to handle support around the clock — cutting response time from 12 hours to 1.5 hours.",
    tags: ["E-commerce", "WhatsApp", "24/7 Support", "Ticket Reduction"],
    metrics: {
      hoursSaved: "20 hrs/week",
      costReduction: "$2,800/mo",
      responseTimeImprovement: "87%",
      satisfaction: "+30%",
    },
    challenge: `ShopDirect was processing 200 orders per day, but their support team couldn't keep up. With a small team working business hours only, customer inquiries piled up — especially at night and on weekends.

Average response time had reached 12 hours. Customers were getting frustrated, and the team was missing inquiries entirely during off-hours. The founder was spending evenings just answering support messages.

"We were losing orders because customers couldn't get answers when they needed them," says David Park, Founder. "People shop at 11 PM, and they expect answers at 11 PM."`,
    solution: `ShopDirect deployed Max OS Cloud on the Growth plan, connecting it to their two highest-volume channels:

**WhatsApp Integration:** Max OS was trained on ShopDirect's product catalog, shipping policies, and common customer questions. It now handles the majority of WhatsApp inquiries instantly — providing order status updates, product information, and return guidance.

**Email Automation:** Max OS monitors the support email inbox 24/7, responding to routine inquiries and escalating complex cases to the human team with full conversation context.

**Product Catalog Training:** Max OS was given detailed information about every product, including specifications, pricing, and availability. This allows it to answer product-specific questions accurately.

The result: customers now get instant answers at any hour, and the human team focuses on complex issues that require personal attention.`,
    implementation: {
      timeline: "1 week",
      steps: [
        { week: "Day 1-2", title: "Channel Integration", description: "WhatsApp Business API and Email connected to Max OS Cloud on Growth plan. Product catalog imported and indexed." },
        { week: "Day 3-4", title: "Training & Configuration", description: "Max OS trained on shipping policies, return procedures, and common customer questions. Response templates configured." },
        { week: "Day 5-7", title: "Go-Live & Monitoring", description: "24/7 coverage activated. Human team notified of escalation protocols. Performance dashboards configured." },
      ],
      channels: ["WhatsApp", "Email"],
    },
    beforeAfter: {
      before: ["12-hour average response time", "Support team only available business hours", "30+ hours/week on routine inquiries", "Lost orders due to slow responses"],
      after: ["1.5-hour average response time (-87%)", "24/7 AI coverage, 365 days/year", "10 hours/week on routine inquiries (-66%)", "Zero lost orders since deployment"],
    },
    results: [
      { value: "40%", label: "Support ticket reduction" },
      { value: "24/7", label: "Customer coverage" },
      { value: "1.5 hrs", label: "Avg response time" },
      { value: "30%", label: "CSAT increase" },
    ],
    testimonial: {
      quote: "Our customers get instant answers at 3am. We haven't lost a single order to slow support since deploying Max OS.",
      name: "David Park",
      role: "Founder",
      photo: "https://images.unsplash.com/photo-1556157382-97eda2d62296?crop=entropy&cs=srgb&fm=jpg&q=85&w=200&h=200&fit=crop",
    },
  },
  {
    slug: "growthlab",
    company: "GrowthLab Agency",
    industry: "Marketing Agency",
    industryTag: "Agency",
    category: "Agency",
    employees: "15 clients",
    headline: "3x client capacity without hiring a single account manager",
    excerpt: "GrowthLab Agency used Max OS Cloud to scale from 15 to 45 clients — tripling capacity while reducing cost per client by 50%.",
    tags: ["Agency", "Scaling", "Reporting", "Client Management"],
    metrics: {
      hoursSaved: "15 hrs/week",
      costReduction: "50%/client",
      responseTimeImprovement: "75%",
      satisfaction: "+45%",
    },
    challenge: `GrowthLab Agency was at a crossroads. They had 15 active clients and a reputation for excellent work, but scaling meant hiring — and hiring meant increasing overhead.

Each account manager was spending 20+ hours per week on routine tasks: generating reports, sending status updates, scheduling meetings, and managing client communication. There was barely enough time for the strategic work that actually drove results.

"We couldn't take on more clients without hiring, but hiring would eat into our margins," says Rebecca Santos, Director. "We needed a way to scale without proportionally increasing headcount."`,
    solution: `GrowthLab Agency deployed Max OS Cloud on the Business plan, creating a comprehensive automation layer across their client management operations:

**Automated Reporting:** Max OS generates weekly and monthly reports for each client automatically. It pulls data from analytics platforms, formats it according to each client's preferences, and delivers it on schedule — no manual work required.

**Client Communication:** Max OS handles routine client communication — status updates, meeting reminders, and follow-up messages. It maintains each client's preferred communication style and schedule.

**Account Coordination:** Max OS monitors project timelines, flags potential delays, and coordinates between team members. It acts as a virtual project coordinator, keeping everything on track.

The impact was immediate: each account manager went from managing 5 clients to managing 15, without working longer hours.`,
    implementation: {
      timeline: "3 weeks",
      steps: [
        { week: "Week 1", title: "Audit & Planning", description: "All 15 client workflows audited. Repetitive tasks identified and documented. Max OS Cloud provisioned on Business plan." },
        { week: "Week 2", title: "Automation Build", description: "Automated reporting, client communication, and account coordination workflows built and tested. Each client's preferences configured." },
        { week: "Week 3", title: "Scale & Onboard", description: "Automation went live for all 15 existing clients. 30 new clients onboarded with automation from day one." },
      ],
      channels: ["Email", "Slack", "Google Meet"],
    },
    beforeAfter: {
      before: ["15 clients max capacity", "20+ hours/week on routine tasks per manager", "High cost per client", "Growth limited by hiring"],
      after: ["45 clients (3x capacity)", "5 hours/week on routine tasks (-75%)", "50% cost per client reduction", "Scalable without proportional hiring"],
    },
    results: [
      { value: "3x", label: "Client capacity (15→45)" },
      { value: "50%", label: "Cost per client reduction" },
      { value: "20 hrs", label: "Saved per account manager/week" },
      { value: "45", label: "Total clients managed" },
    ],
    testimonial: {
      quote: "We tripled our client roster without hiring a single new account manager. Max OS handles the routine so our team focuses on strategy.",
      name: "Rebecca Santos",
      role: "Director",
      photo: "https://images.unsplash.com/photo-1611111082664-534b2cd71d6f?crop=entropy&cs=srgb&fm=jpg&q=85&w=200&h=200&fit=crop",
    },
  },
];

export function getCaseStudyBySlug(slug) {
  return CASE_STUDIES.find((cs) => cs.slug === slug);
}

export function getRecentCaseStudies(count = 2) {
  return CASE_STUDIES.slice(0, count);
}

export const CASE_STUDY_CATEGORIES = ["All", "SaaS", "E-commerce", "Agency"];
